import { Star, Flame, Trophy, Info, ChevronRight, FlaskConical } from 'lucide-react';
import { Link } from 'react-router-dom';
import InfoCardShell from './InfoCardShell';
import useGuideLang from '../lib/useGuideLang';

/**
 * Iteration-40 rewrite of the streaks-and-rewards explainer.
 *
 * The Home screen (`HomeStreaksLine`) now shows a SINGLE-LINE pointer to
 * this content on Profile. The full explanation lives on Profile as a
 * Hindi-by-default card with a Meaning?-style toggle for English, plus a
 * shared "Read to me" TTS button that speaks whichever language is
 * currently visible using the same ElevenLabs pipeline as reading
 * practice.
 *
 * The Hindi copy uses everyday parent+child friendly words — kept
 * intentionally short so the audio narration finishes in ~30 s.
 */

// Kid-friendly Hindi copy. Reads like a warm parent explaining, not
// like textbook grammar. Each concept is one paragraph so the "Read to
// me" narration sounds like three natural beats. All numeric milestones
// are spelled out so the TTS pronounces them clearly.
const HI = {
  intro: 'यहाँ हम बताते हैं कि सितारे, स्ट्रीक और ईनाम कैसे काम करते हैं।',
  stars: {
    title: 'सितारे',
    body: 'जब बच्चा एक हिस्सा पढ़ता है, उसे सितारे मिलते हैं। जितना अच्छा पढ़ोगे, उतने ज़्यादा सितारे। सितारे कभी कम नहीं होते — हमेशा जुड़ते ही रहते हैं।',
  },
  streak: {
    title: 'दिन-स्ट्रीक',
    body: 'लगातार दिनों तक पढ़ो — यही स्ट्रीक है। दो दिन पढ़ा तो दो का स्ट्रीक, तीन दिन पढ़ा तो तीन का। एक दिन छूट गया तो स्ट्रीक फिर से एक से शुरू होगा। तीन, पाँच, सात, दस, चौदह, इक्कीस और तीस दिन पर एक बड़ा जश्न मिलता है — और उसके बाद हर तीस दिन पर।',
  },
  reward: {
    title: 'ईनाम',
    body: 'बड़े लोग एक ईनाम तय करते हैं, जैसे “आइसक्रीम की सैर”। उसमें एक अंक का लक्ष्य होता है, मान लो सौ अंक, और एक समय-सीमा होती है, मान लो इस महीने के अंदर। समय के अंदर अंक पूरे हो जाएँ तो ईनाम खुल जाता है। अगर समय-सीमा निकल जाए, तो ईनाम रुक जाता है — बड़े लोग उसे दोबारा शुरू कर सकते हैं।',
  },
  // Iter-51.49 — moved from Profile "Trial sessions" card. Sits with
  // the rest of the stars/streak/reward system because trial is the
  // parent-facing lever that PROTECTS those numbers.
  trial: {
    title: 'ट्रायल सेशन',
    body: 'अगर आप (अभिभावक) खुद कोई पठन या Beat the Bot सेशन आज़माना चाहें, तो उसे "trial" के रूप में मार्क कर सकते हैं। ट्रायल सेशन इतिहास में तो दिखते हैं, लेकिन बच्चे के अंक, streak, बेस्ट स्कोर और जीत-दर में नहीं गिने जाते — तो आपकी अपनी practice कभी बच्चे के आँकड़ों को छूएगी नहीं।',
  },
};

const EN = {
  intro: 'Here is how stars, streaks and rewards work in this app.',
  stars: {
    title: 'Stars',
    body: 'When a student reads a chunk, they earn stars. The better they read, the more stars they get. Stars never go down — they only add up over time.',
  },
  streak: {
    title: 'Day streak',
    body: 'Read on many days in a row — that is your streak. Read two days = 2 streak. Miss a day and the streak starts back at 1. At 3, 5, 7, 10, 14, 21 and 30 days you get a big celebration — and then every 30 days after that.',
  },
  reward: {
    title: 'Rewards',
    body: 'A grown-up sets a reward like "Ice cream trip" with a points target (say 100 points) and a time window (say this month). Reach the target inside the window and the reward unlocks. Miss the deadline and the reward pauses — a grown-up can start it again anytime.',
  },
  // Iter-51.49 — moved from Profile "Trial sessions" card. Kept in
  // this same pack so the /rewards page becomes the single place to
  // learn about the whole points/streak/reward loop AND the parent
  // safety lever that protects it.
  trial: {
    title: 'Trial sessions',
    body: 'If you (the parent/guardian) want to try out a reading or Beat the Bot session yourself, mark that session as a "trial" using the trial option on the session card. Trial sessions stay visible in the session history but are excluded from your child\'s scores, streaks, best score, and win rate — so your own practice runs never affect your child\'s stats.',
  },
};

function fullPassage(pack) {
  return [
    pack.intro,
    pack.stars.title + '. ' + pack.stars.body,
    pack.streak.title + '. ' + pack.streak.body,
    pack.reward.title + '. ' + pack.reward.body,
    pack.trial.title + '. ' + pack.trial.body,
  ].join(' ');
}

/**
 * Home line — a tiny nudge pointing readers to the full explainer on
 * the Profile screen. Replaces the long iter-38 explainer banner.
 */
export function HomeStreaksLine({ lang = 'en' }) {
  // Iter-52.17 — Home-toggle-aware. When the parent has flipped the
  // Home lang toggle to Hindi we swap the label so the entire Home
  // page stays coherent in one language.
  const label = lang === 'hi'
    ? 'सितारे और ईनाम के बारे में Profile में पढ़ें'
    : 'Know about streaks and rewards in Profile';
  return (
    <Link
      to="/profile"
      data-testid="home-streaks-nudge"
      className={`mb-6 inline-flex items-center gap-2 text-sm font-bold text-[#5B8DEF] hover:underline ${lang === 'hi' ? 'font-hi' : ''}`}
    >
      <Info size={14} strokeWidth={2.6}/>
      {label}
      <ChevronRight size={14} strokeWidth={2.6}/>
    </Link>
  );
}

/**
 * ProfileStreaksExplainer — full three-concept explainer for the Profile
 * screen. Hindi by default (matches the target audience), with a toggle
 * that reuses the "Meaning?" translation copy convention. Read-to-me
 * always narrates whichever language is currently visible so no one
 * gets confused if the two versions ever drift.
 */
export function ProfileStreaksExplainer() {
  // Iter-51.14: language driven by the shared page-level toggle
  // instead of a card-local Hindi-default state. Removes the built-in
  // Read-to-me + "English में पढ़ें" buttons that used to live here.
  const [lang] = useGuideLang();
  const pack = lang === 'hi' ? HI : EN;

  return (
    <InfoCardShell
      testId="streaks-explainer"
      icon={
        <div className="w-10 h-10 rounded-2xl bg-[#F4B740] text-[#2B2140] flex items-center justify-center">
          <Info size={20} strokeWidth={2.8}/>
        </div>
      }
      title={lang === 'hi' ? 'सितारे, streaks और ईनाम' : 'Stars, streaks & rewards'}
      subtitle={lang === 'hi' ? 'हर पढ़ाई कैसे जश्न में बदलती है' : 'How reading turns into celebrations'}
      contentLang={lang}
      defaultOpen
    >
      <p data-testid="streaks-explainer-intro" className="text-slate-600 leading-relaxed">
        {pack.intro}
      </p>
      <div className="space-y-4">
        <Row
          icon={<Star size={20} strokeWidth={2.5} className="text-white fill-white"/>}
          bg="bg-[#FF7A45]"
          title={pack.stars.title}
          body={pack.stars.body}
        />
        <Row
          icon={<Flame size={20} strokeWidth={2.5} className="text-white"/>}
          bg="bg-[#4A9B7F]"
          title={pack.streak.title}
          body={pack.streak.body}
        />
        <Row
          icon={<Trophy size={20} strokeWidth={2.5} className="text-[#2B2140]"/>}
          bg="bg-[#F4B740]"
          title={pack.reward.title}
          body={pack.reward.body}
        />
        {/* Iter-51.49 — Trial sessions row. Moved here from the
            standalone Profile card so all the "how points work"
            explainer lives in one page. FlaskConical matches the
            trial toggle icon used in QuizHistory/ReadingHistory. */}
        <Row
          testid="streaks-explainer-trial"
          icon={<FlaskConical size={20} strokeWidth={2.5} className="text-white"/>}
          bg="bg-[#5B8DEF]"
          title={pack.trial.title}
          body={pack.trial.body}
        />
      </div>
    </InfoCardShell>
  );
}

function Row({ icon, bg, title, body, testid }) {
  return (
    <div data-testid={testid} className="flex gap-3 items-start">
      <div className={`w-10 h-10 rounded-2xl ${bg} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-extrabold text-base">{title}</div>
        <p className="text-slate-600 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
